import { BRAND } from '@console/shared'
import type { ReactNode } from 'react'
import { CopyButton } from '../components/ui/misc'
import { useConfig, useModels } from '../lib/api'

function Code({ children }: { children: string }) {
  return (
    <div className="relative my-3 overflow-hidden rounded-md border border-line">
      <div className="absolute right-1 top-1">
        <CopyButton value={children} label="" />
      </div>
      <pre className="overflow-x-auto bg-surface-sunken p-3 pr-10 text-[12px] leading-relaxed text-ink">{children}</pre>
    </div>
  )
}

function Section({ id, title, children }: { id: string; title: string; children: ReactNode }) {
  return (
    <section id={id} className="scroll-mt-6 border-b border-line-subtle py-7 last:border-0">
      <h2 className="mb-3 text-[16px] font-semibold text-ink">{title}</h2>
      <div className="space-y-3 text-[13.5px] leading-[1.65] text-ink-secondary [&_code]:rounded [&_code]:bg-surface-sunken [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_code]:text-ink">
        {children}
      </div>
    </section>
  )
}

function Table({ head, rows }: { head: string[]; rows: ReactNode[][] }) {
  return (
    <div className="card my-3 overflow-x-auto">
      <table className="w-full min-w-[520px]">
        <thead className="border-b border-line">
          <tr>
            {head.map((h) => (
              <th key={h} className="th">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-line-subtle last:border-0">
              {r.map((c, j) => (
                <td key={j} className="td align-top !text-[12.5px]">
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TOC = [
  ['overview', 'Overview'],
  ['auth', 'Authentication'],
  ['decide', 'POST /v1/decide'],
  ['questions', 'Question types'],
  ['response', 'Reading the response'],
  ['systemone', 'TypeSafe compatibility'],
  ['models', 'Models and pricing'],
  ['errors', 'Errors'],
  ['limits', 'Limits'],
  ['tips', 'Writing good questions'],
] as const

export function DocsPage() {
  const base = useConfig().data?.publicApiUrl ?? window.location.origin
  const { data: models } = useModels()
  const model = models?.data.find((m) => m.isDefault)?.id ?? BRAND.defaultModelId

  return (
    <div className="flex h-full overflow-hidden">
      <nav className="hidden w-52 shrink-0 overflow-y-auto border-r border-line px-3 py-6 lg:block">
        <div className="mb-2 px-2 text-[10.5px] font-semibold uppercase tracking-wider text-ink-muted">API guide</div>
        {TOC.map(([id, label]) => (
          <a key={id} href={`#${id}`} className="block rounded-md px-2 py-1 text-[13px] text-ink-secondary hover:bg-surface-hover hover:text-ink">
            {label}
          </a>
        ))}
      </nav>
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="mx-auto max-w-3xl px-5 py-4 md:px-8">
          <Section id="overview" title="Overview">
            <p>
              {BRAND.modelFamily} models read a <b className="text-ink">state</b> (an agent trace, a conversation, a document, a tool catalogue, or any
              JSON or text) and answer <b className="text-ink">typed questions</b> about it with a probability for every option, in one forward pass.
              There is no generated text, so an answer is never malformed, and asking ten questions about one state costs little more than asking one.
            </p>
            <p>Two common uses: judging every run of an agent in production (evals), and making decisions inside an agent: routing, tool choice, continue or stop, guardrails.</p>
            <p>
              Base URL: <code>{base}</code>
            </p>
          </Section>

          <Section id="auth" title="Authentication">
            <p>
              Create a key under <b className="text-ink">API Keys</b> and send it as a bearer token. Keys belong to your organization and every call is billed to its
              credits. Keep keys server-side.
            </p>
            <Code>{`Authorization: Bearer trodo_sk_…`}</Code>
          </Section>

          <Section id="decide" title="POST /v1/decide">
            <Code>{`curl ${base}/v1/decide \\
  -H "Authorization: Bearer $TRODO_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
  "model": "${model}",
  "state": {
    "system_prompt": "Never promise refunds over $100 without a manager.",
    "messages": [
      {"role": "user", "content": "I want my $250 back."},
      {"role": "assistant", "content": "Done, I have refunded the full $250."}
    ]
  },
  "questions": {
    "followed_policy": {"type": "noul", "instructions": "Did the agent follow its system prompt?"},
    "next_step": {
      "type": "choice",
      "instructions": "What should happen next?",
      "criteria": {"escalate": "Hand to a manager", "close": "Close the ticket", "ask_user": "Ask the user for more detail"}
    }
  }
}'`}</Code>
            <Table
              head={['Field', 'Type', 'Description']}
              rows={[
                [<code>model</code>, 'string', <>Optional. Defaults to <code>{model}</code>. See Models.</>],
                [<code>state</code>, 'any JSON or string', 'What the questions are about. Required.'],
                [<code>questions</code>, 'object', 'Name → question. 1 or more; names are 1–128 characters and come back as the keys of answers.'],
                [<code>options.abstain</code>, 'boolean', <>Default <code>true</code>: every question can answer “can't tell” when the state lacks the evidence.</>],
                [<code>options.calibration</code>, <code>global | none</code>, 'Default global: calibrated probabilities. none returns raw model probabilities.'],
              ]}
            />
          </Section>

          <Section id="questions" title="Question types">
            <Table
              head={['type', 'Asks', 'criteria', 'Answer field']}
              rows={[
                [<code>noul</code>, 'a yes/no proposition', <>optional <code>{'{"yes": "…", "no": "…"}'}</code> describing each case</>, <><code>noul</code>: P(yes)</>],
                [<code>choice</code>, 'pick one of N (2–255)', <>list of names, or <code>{'{name: description}'}</code> (descriptions help)</>, <><code>choice</code> + <code>probabilities</code></>],
                [<code>score</code>, 'an ordered rating (2–10 levels)', 'list of level descriptions, lowest first', <><code>score</code>: expected level (0-based) + <code>probabilities</code></>],
                [<code>extract</code>, 'a value stated in the state', <>none; optional <code>{'{"extract": {"kind": "amount"}}'}</code> narrows the candidates</>, <><code>present</code>, <code>value</code>, <code>span</code> + <code>candidates</code></>],
              ]}
            />
            <p>
              <code>abstain</code> is reserved: it is added to every question automatically and reported separately.
            </p>
          </Section>

          <Section id="response" title="Reading the response">
            <Code>{`{
  "model": "${model}",
  "answers": {
    "followed_policy": {"type": "noul", "noul": 0.04, "abstain": 0.01},
    "next_step": {
      "type": "choice", "choice": "escalate",
      "probabilities": {"escalate": 0.88, "close": 0.03, "ask_user": 0.09},
      "confidence": 0.82, "abstain": 0.02
    }
  },
  "usage": {"input_tokens": 142, "output_tokens": 0},
  "latency_ms": 41.7
}`}</Code>
            <Table
              head={['Field', 'Meaning']}
              rows={[
                [<code>probabilities</code>, 'Calibrated distribution over your options (abstain excluded, renormalised). An answer given at 0.9 is right about 90% of the time.'],
                [<code>confidence</code>, <>How peaked the distribution is: <code>(K·p_max − 1)/(K − 1)</code>, 1 = all mass on one option, 0 = uniform.</>],
                [<code>abstain</code>, '“Can\'t tell” mass: how much the model thinks the state lacks the evidence. Route high-abstain answers to a person or a larger model.'],
                [<code>usage.input_tokens</code>, 'What you are billed for. Output is free.'],
              ]}
            />
            <p>Every response carries an <code>x-request-id</code> header; include it when you contact support.</p>
          </Section>

          <Section id="systemone" title="TypeSafe compatibility">
            <p>
              <code>POST /v1/systemone</code> accepts and returns the TypeSafe System One shape (<code>{'{model, state, questions}'}</code> →{' '}
              <code>{'{model, answers, usage}'}</code>), so existing clients switch by changing the base URL and key. Abstain mass is reported under{' '}
              <code>x_trodo</code> on each answer.
            </p>
          </Section>

          <Section id="models" title="Models and pricing">
            <Table
              head={['Model', 'Status', 'Context', 'Price']}
              rows={(models?.data ?? []).map((m) => [
                <code>{m.id}</code>,
                m.status === 'active' || m.status === 'preview' ? 'Available' : m.status,
                `${(m.contextTokens / 1024).toFixed(0)}K tokens`,
                `$${m.usdPerMillionInputTokens} per 1M input tokens`,
              ])}
            />
            <p>
              <code>GET /v1/models</code> lists them programmatically. Failed requests are never billed.
            </p>
          </Section>

          <Section id="errors" title="Errors">
            <p>
              Errors are JSON: <code>{'{"error": {"type": "…", "message": "…", "details": ["…"]}}'}</code>.
            </p>
            <Table
              head={['Status', 'type', 'What to do']}
              rows={[
                ['401', <code>invalid_api_key</code>, 'Check the key; revoked keys stop working within seconds.'],
                ['402', <code>insufficient_credits</code>, 'Add credits or turn on auto top-up under Billing.'],
                ['402', <code>budget_exceeded</code>, "A monthly budget (the organization's or this key's) is used up. The message says which and when it resets."],
                ['403', <code>organization_suspended</code>, 'Contact support.'],
                ['404', <code>model_not_found</code>, 'Use an id from /v1/models.'],
                ['422', <code>invalid_request</code>, <>The <code>details</code> list names each problem, e.g. <code>questions.route.criteria: choice needs at least 2 options</code>.</>],
                ['429', <code>rate_limited</code>, 'Back off and retry after the Retry-After header.'],
                ['500', <code>internal_error</code>, 'Retry. If it keeps happening, contact support with the x-request-id.'],
                ['502', <code>model_error</code>, 'The model returned an error. Retry with backoff. Not billed.'],
                ['502 / 503 / 504', <code>model_unavailable</code>, 'The model could not be reached in time. Retry; the first call after a quiet spell can take up to a minute.'],
                ['529', <code>overloaded</code>, 'Retry after the Retry-After header. Not billed.'],
              ]}
            />
          </Section>

          <Section id="limits" title="Limits">
            <ul className="list-disc space-y-1 pl-5">
              <li>Request body up to 4 MB; state up to the model's context (16K tokens for {BRAND.defaultModelId}, 32K for {BRAND.defaultModelId}-flash).</li>
              <li>choice: 2–255 options; score: 2–10 levels; extract: 1–128 candidate spans (default 48).</li>
              <li>A call on a few thousand tokens usually takes about a second; the first call after a quiet spell can take up to a minute, so use a client timeout of at least 120 s.</li>
              <li>600 requests per minute per key by default. Ask us for more.</li>
            </ul>
          </Section>

          <Section id="tips" title="Writing good questions">
            <ul className="list-disc space-y-1 pl-5">
              <li>Ask one thing per question. Five atomic questions beat one compound one, and cost about the same.</li>
              <li>Describe choice options and score levels in words; the model reads them alongside the state.</li>
              <li>Keep “can't tell” on for evals; treat a high abstain as a signal, not an error.</li>
              <li>Use the confidence to cascade: act on confident answers, send the rest to a person or a larger model.</li>
            </ul>
          </Section>
        </div>
      </div>
    </div>
  )
}
